/**
 * Access-denied event bus + burst-dedupe registry.
 *
 * Plain TS (no React) so non-React code -- the axios response interceptor --
 * can announce a 403, and a single mounted `<AccessDeniedHost />` (subscribed
 * here) turns it into a dialog. When no host is mounted (other entrypoints), a
 * registered fallback (a plain snackbar) runs instead; if nothing is
 * registered, the notification safely no-ops.
 *
 * Burst dedupe is the whole point of routing through this module rather than
 * calling the host directly: a single page action (a CRM box-open fans out
 * ~58 requests) can produce many parallel 403s. We must present ONE dialog and
 * coalesce the rest, both during the initial synchronous burst (before React
 * has flipped the dialog-open state) and for a short trailing window after the
 * dialog closes.
 */
/**
 * Coalescing window (ms). Covers both the initial synchronous fan-out burst
 * (many 403s land within a few ms, before the host flips `dialogOpen`) and the
 * trailing "same endpoint again right after close" repeat.
 */
export const ACCESS_DENIED_DEDUPE_WINDOW_MS = 2000;
const listeners = new Set();
let fallback = null;
// Presentation state, tracked synchronously so a burst is deduped without
// waiting on React. `dialogOpen` is authoritative while a dialog is on screen;
// the timestamps handle the burst before it opens and the window after it closes.
let dialogOpen = false;
let lastEmitAt = Number.NEGATIVE_INFINITY;
let lastCloseAt = Number.NEGATIVE_INFINITY;
let lastUrl;
// Injectable clock so the dedupe logic is unit-testable without real timers.
let now = () => Date.now();
/** TEST ONLY: override the clock. */
export const __setAccessDeniedClock = (clock) => {
    now = clock;
};
/** TEST ONLY: reset all module state between cases. */
export const __resetAccessDeniedBus = () => {
    listeners.clear();
    fallback = null;
    dialogOpen = false;
    lastEmitAt = Number.NEGATIVE_INFINITY;
    lastCloseAt = Number.NEGATIVE_INFINITY;
    lastUrl = undefined;
    now = () => Date.now();
};
/** Whether an `<AccessDeniedHost />` is currently subscribed. */
export const hasAccessDeniedHost = () => listeners.size > 0;
/**
 * Subscribe a host to denial events. Returns an unsubscribe function. Only one
 * host is expected in practice, but multiple are handled (all are notified).
 */
export const subscribeAccessDenied = (listener) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
/**
 * Register the no-host fallback (a plain error snackbar). Kept out of this
 * module's imports so the bus stays React/notistack-free and trivially
 * testable; wired up once at app bootstrap.
 */
export const setAccessDeniedFallback = (handler) => {
    fallback = handler;
};
/** The host reports when its dialog becomes visible. */
export const markAccessDeniedDialogOpen = () => {
    dialogOpen = true;
};
/** The host reports when its dialog closes, starting the trailing window. */
export const markAccessDeniedDialogClosed = () => {
    dialogOpen = false;
    lastCloseAt = now();
};
/**
 * Should a fresh 403 open a NEW presentation, or be coalesced into one already
 * showing / just shown? Pure so it can be reasoned about and tested directly.
 */
const shouldPresent = (info, at) => {
    // A dialog is on screen -- absorb everything into it.
    if (dialogOpen) {
        return false;
    }
    // Initial burst: the fan-out's other 403s arrive within a couple ms, before
    // the host has flipped `dialogOpen`. Suppress them all after the first.
    if (at - lastEmitAt < ACCESS_DENIED_DEDUPE_WINDOW_MS) {
        return false;
    }
    // Trailing: the same endpoint denied again right after the user closed the
    // dialog (e.g. a retrying poll) shouldn't immediately re-pop it.
    if (at - lastCloseAt < ACCESS_DENIED_DEDUPE_WINDOW_MS && info.url !== undefined && info.url === lastUrl) {
        return false;
    }
    return true;
};
/**
 * Whether a 403 presentation is currently active/recent for `url` -- used by the
 * error-toast path to avoid double-presenting (dialog + toast for the same
 * denial). True while a dialog is open, or within the dedupe window of the last
 * presentation.
 */
export const isAccessDeniedPresenting = (url) => {
    if (dialogOpen) {
        return true;
    }
    if (now() - lastEmitAt < ACCESS_DENIED_DEDUPE_WINDOW_MS) {
        // Same-endpoint (or unknown-endpoint) repeat of the just-presented denial.
        return url === undefined || url === lastUrl;
    }
    return false;
};
/**
 * Announce a denied (403) request. Coalesces per the dedupe rules; on a fresh
 * presentation, notifies the host(s) or, if none, the fallback. Safe to call
 * from anywhere -- a no-op when nothing is listening and no fallback is set.
 */
export const notifyAccessDenied = (info) => {
    const at = now();
    if (!shouldPresent(info, at)) {
        return;
    }
    lastEmitAt = at;
    lastUrl = info.url;
    if (listeners.size > 0) {
        listeners.forEach((listener) => {
            listener(info);
        });
        return;
    }
    // No host mounted: degrade to a plain snackbar if one is wired up.
    fallback === null || fallback === void 0 ? void 0 : fallback(info);
};
